// biz 플랜 전용 에러코드(710/711/712/713 + 494) 식별·게이트·문구 매핑 단일 소스. (OMD-27)
//
// - 710 계약 만료 → contractExpired 문구("계약이 유효하지 않거나 만료되었습니다.") — 711 과 분리(2026-07-17).
// - 711 계약 해지 / 494(HTTP status 494 + body code 494) → contractTerminated 문구("서비스 계약이 종료되었습니다.").
//   ※ 494 body code 는 711 과 동일하게 처리(안내 문구 + 강제 로그아웃). 2026-07-16 추가.
// - 712 서비스 접근 권한 없음 / 713(712 와 동일 문구) → accessDenied 문구.
// - officemail 백엔드만 방출하며, 백엔드가 QC/dev 에만 반영되어 운영/스테이징에는 미반영이므로
//   프론트 분기도 환경 게이트로 완전 비활성화한다.
// - 494 body code 는 항상 HTTP status 494 컨텍스트(채널2 감지)에서만 판정되며, 두 감지 지점
//   (restClient processResponse / jwt customFetch)이 모두 `status===494` 로 게이트한다.

export const BIZ_ERROR_CODES = {
  CONTRACT_EXPIRED: 710, // 계약 만료
  CONTRACT_TERMINATED: 711, // 계약 해지
  ACCESS_DENIED: 712, // 서비스 접근 권한 없음
  ACCESS_DENIED_713: 713, // 713 — 712 와 동일 문구(서비스 접근 권한 없음)
  WORKSPACE_DISABLED: 494, // status 494 + body code 494 — 711 과 동일 문구(contractTerminated)
} as const

// 코드별 안내 문구 매핑(i18n 키 + 한국어 fallback). 이 map 이 isBizErrorCode / 키 / fallback 의 단일 소스.
// - 710 → contractExpired(계약 만료), 711/494 → contractTerminated(서비스 계약 종료), 712/713 → accessDenied.
const BIZ_ERROR_MESSAGES: Readonly<Record<number, { key: string; fallback: string }>> = {
  [BIZ_ERROR_CODES.CONTRACT_EXPIRED]: {
    key: 'biz.error.contractExpired',
    fallback: '계약이 유효하지 않거나 만료되었습니다.',
  },
  [BIZ_ERROR_CODES.CONTRACT_TERMINATED]: {
    key: 'biz.error.contractTerminated',
    fallback: '서비스 계약이 종료되었습니다.',
  },
  [BIZ_ERROR_CODES.WORKSPACE_DISABLED]: {
    key: 'biz.error.contractTerminated',
    fallback: '서비스 계약이 종료되었습니다.',
  },
  [BIZ_ERROR_CODES.ACCESS_DENIED]: {
    key: 'biz.error.accessDenied',
    fallback: '서비스 접근 권한이 없습니다.',
  },
  [BIZ_ERROR_CODES.ACCESS_DENIED_713]: {
    key: 'biz.error.accessDenied',
    fallback: '서비스 접근 권한이 없습니다.',
  },
}

// code 는 채널에 따라 number(런타임 494 body, 답변 §C) 또는 string(SSO `params.code`,
// LandingDataForLogin.params.code?: string) 로 온다. 두 표현 모두 안전하게 숫자로 정규화한다.
const toNumericCode = (code?: number | string | null): number | undefined => {
  if (typeof code === 'number') {
    return code
  }
  if (typeof code === 'string' && code.trim() !== '') {
    const parsed = Number(code)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  return undefined
}

export const isBizErrorCode = (code?: number | string | null): boolean => {
  const numeric = toNumericCode(code)
  return numeric !== undefined && numeric in BIZ_ERROR_MESSAGES
}

// 환경 게이트 — QC / development(log·debug·proxydev 포함) / local 에서만 활성.
// production / staging / proxystaging 에서는 false 로 정적 평가되어 tree-shaking 된다.
// (참고: vars.ts 의 `isQc || isDevelop` 와 동일 조합. packages 에서는 앱 vars.ts 를 import 할 수
//  없으므로 raw 전역 상수를 직접 사용한다 — 타입은 globals.d.ts 에 선언됨.)
//
// provider(officemail) 게이트는 두지 않는다: 710/711/712 는 백엔드가 officemail 배포에서만
// 방출함을 보장하므로(답변 문서 §E) 중복이고, 저장된 provider 값이 정확히 'officemail' 이 아닐 때
// 정당한 강제 로그아웃을 억제할 위험이 있다. 환경 게이트 + isBizErrorCode 로 충분하다.
export const BIZ_ERROR_CODES_ENABLED = __ENV_QC__ || __ENV_DEVELOPMENT__ || __ENV_LOCAL__

// 코드별 i18n 키 매핑 (710=contractExpired, 711/494=contractTerminated, 712/713=accessDenied).
export const getBizErrorMessageKey = (code: number | string): string =>
  BIZ_ERROR_MESSAGES[toNumericCode(code) ?? -1]?.key ?? 'biz.error.contractTerminated'

// i18n 초기화 전 호출 대비 한국어 fallback (raw 키 노출 방지).
export const getBizErrorMessageFallback = (code: number | string): string =>
  BIZ_ERROR_MESSAGES[toNumericCode(code) ?? -1]?.fallback ?? '서비스 계약이 종료되었습니다.'
